import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Handshake, TrendingUp, Users, ShieldCheck, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import useDocumentMeta from "@/lib/useDocumentMeta";

const SITE_URL = "https://juketransfers.hr";

const FLEET_TYPES = [
  { value: "boats",           label: "Boats · Charter · Yachts" },
  { value: "quads_scooters",  label: "Quads & Scooters · Vespas" },
  { value: "rent_a_car",      label: "Rent-a-Car · Fleet vehicles" },
  { value: "villas_stays",    label: "Villas & Stays" },
  { value: "activities",      label: "Activities · Wine · Adventure" },
  { value: "other",           label: "Other" },
];

const VALUE_PROPS = [
  {
    Icon: TrendingUp,
    title: "High-intent guests, delivered",
    body:
      "Every referral we send has already booked a premium transfer with us — they are actively spending on their trip and looking for the next best thing to do.",
  },
  {
    Icon: Users,
    title: "Concierge-grade introductions",
    body:
      "Our drivers and dispatch team recommend you personally to guests who match your service. No cold traffic, no bots.",
  },
  {
    Icon: ShieldCheck,
    title: "Straightforward commission",
    body:
      "Fair split on every completed booking. Monthly settlement, transparent reporting, no hidden fees. You keep control of pricing and availability.",
  },
];

export default function PartnersPage() {
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    company_name: "", contact_person: "", email: "", whatsapp: "",
    fleet_type: "", fleet_size: "", region: "", website: "", pitch: "",
  });

  useDocumentMeta({
    title: "Partner with Juke Transfers — Supplier Portal · Split, Croatia",
    description:
      "Own a fleet of boats, quads, scooters, cars or villas around Split? Partner with Juke Transfers and receive concierge referrals from premium transfer guests. Apply in 60 seconds.",
    canonical: `${SITE_URL}/partners`,
    ogImage: `${SITE_URL}/og-image.png`,
    ogType: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "BusinessAudience",
      "audienceType": "Fleet owners, charter operators, rental agencies",
      "geographicArea": { "@type": "AdministrativeArea", "name": "Dalmatia, Croatia" },
    },
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_person || !form.email ||
        !form.whatsapp || !form.fleet_type) {
      toast.error("Please fill in the required fields.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/partners/apply", form);
      setSubmitted(true);
      toast.success("Application received — we'll be in touch shortly.");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not send application. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      <Header />

      {/* Hero */}
      <section className="relative pt-40 pb-16 px-6 lg:px-10 border-b border-white/5 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{
            background:
              "radial-gradient(60% 60% at 15% 20%, rgba(197,160,89,0.14) 0%, rgba(0,0,0,0) 60%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#C5A059] mb-4">
            Supplier Portal · Partner with Us
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white font-light tracking-tight max-w-4xl">
            Send us your best inventory.<br />
            <span className="text-[#C5A059] italic font-semibold">We&apos;ll send you our best guests.</span>
          </h1>
          <p className="mt-6 text-zinc-400 max-w-2xl text-base leading-relaxed">
            Juke Transfers moves premium travellers all across Dalmatia. When they ask
            us &ldquo;what next?&rdquo; — we want to answer with you.
            If you operate boats, quads, scooters, rental cars, villas or activities
            around Split, apply below and we&apos;ll set up a partnership call.
          </p>
        </div>
      </section>

      {/* Value props */}
      <section className="px-6 lg:px-10 pt-16 pb-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
          {VALUE_PROPS.map(({ Icon, title, body }) => (
            <div
              key={title}
              className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6 hover:border-[#C5A059]/40 transition-colors"
            >
              <div className="h-11 w-11 rounded-full border border-[#C5A059]/40 bg-[#C5A059]/5 flex items-center justify-center">
                <Icon className="w-5 h-5 text-[#C5A059]" />
              </div>
              <h3 className="mt-4 font-display text-xl text-white font-light tracking-tight">
                {title}
              </h3>
              <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Application form */}
      <section
        id="apply"
        data-testid="partner-apply-section"
        className="flex-1 px-6 lg:px-10 pt-8 pb-24"
      >
        <div className="max-w-3xl mx-auto">
          {submitted ? (
            <div
              data-testid="partner-submitted-panel"
              className="rounded-2xl border border-[#C5A059]/40 bg-[#0a0a0a] p-10 lg:p-14 text-center"
            >
              <div className="h-14 w-14 mx-auto rounded-full bg-[#C5A059]/10 border border-[#C5A059]/40 flex items-center justify-center">
                <Handshake className="w-6 h-6 text-[#C5A059]" />
              </div>
              <h2 className="mt-6 font-display text-3xl text-white font-light tracking-tight">
                Thank you — application received.
              </h2>
              <p className="mt-3 text-sm text-zinc-400 max-w-xl mx-auto leading-relaxed">
                Our partnerships team reviews new suppliers within 2 business days.
                We&apos;ll reach out on WhatsApp or by email to schedule a short onboarding call.
              </p>
              <a
                href="/"
                className="inline-flex items-center gap-2 mt-8 h-11 px-6 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black text-[11px] uppercase tracking-[0.25em] font-semibold"
              >
                Back to home <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="partner-apply-form"
              className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6 lg:p-10 space-y-5"
            >
              <div>
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#C5A059]">Apply to join</p>
                <h2 className="mt-1 font-display text-2xl lg:text-3xl text-white font-light tracking-tight">
                  Fleet Owner Application
                </h2>
                <p className="mt-2 text-xs text-zinc-500">
                  Fields marked <span className="text-[#C5A059]">*</span> are required.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Company name" required>
                  <Input
                    data-testid="partner-company-input"
                    value={form.company_name}
                    onChange={(e) => update("company_name", e.target.value)}
                    placeholder="e.g. Adriatic Marine d.o.o."
                    className="bg-zinc-900 border-white/10 h-11 text-sm"
                  />
                </Field>
                <Field label="Contact person" required>
                  <Input
                    data-testid="partner-contact-input"
                    value={form.contact_person}
                    onChange={(e) => update("contact_person", e.target.value)}
                    placeholder="Full name"
                    className="bg-zinc-900 border-white/10 h-11 text-sm"
                  />
                </Field>
                <Field label="Email" required>
                  <Input
                    data-testid="partner-email-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@yourcompany.hr"
                    className="bg-zinc-900 border-white/10 h-11 text-sm"
                  />
                </Field>
                <Field label="WhatsApp" required>
                  <Input
                    data-testid="partner-whatsapp-input"
                    value={form.whatsapp}
                    onChange={(e) => update("whatsapp", e.target.value)}
                    placeholder="+385 …"
                    className="bg-zinc-900 border-white/10 h-11 text-sm"
                  />
                </Field>
                <Field label="Fleet type" required>
                  <Select
                    value={form.fleet_type}
                    onValueChange={(v) => update("fleet_type", v)}
                  >
                    <SelectTrigger
                      data-testid="partner-fleet-type-select"
                      className="bg-zinc-900 border-white/10 h-11 text-sm"
                    >
                      <SelectValue placeholder="Select your fleet type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0d0d0d] border-white/10 text-zinc-100">
                      {FLEET_TYPES.map((f) => (
                        <SelectItem
                          key={f.value}
                          value={f.value}
                          data-testid={`partner-fleet-${f.value}`}
                          className="text-sm"
                        >
                          {f.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Fleet size (approx.)">
                  <Input
                    data-testid="partner-fleet-size-input"
                    value={form.fleet_size}
                    onChange={(e) => update("fleet_size", e.target.value)}
                    placeholder="e.g. 12 speedboats"
                    className="bg-zinc-900 border-white/10 h-11 text-sm"
                  />
                </Field>
                <Field label="City / Region">
                  <Input
                    data-testid="partner-region-input"
                    value={form.region}
                    onChange={(e) => update("region", e.target.value)}
                    placeholder="Split, Trogir, Hvar…"
                    className="bg-zinc-900 border-white/10 h-11 text-sm"
                  />
                </Field>
                <Field label="Website">
                  <Input
                    data-testid="partner-website-input"
                    value={form.website}
                    onChange={(e) => update("website", e.target.value)}
                    placeholder="https://…"
                    className="bg-zinc-900 border-white/10 h-11 text-sm"
                  />
                </Field>
              </div>

              <Field label="Tell us more (optional)">
                <Textarea
                  data-testid="partner-pitch-input"
                  value={form.pitch}
                  onChange={(e) => update("pitch", e.target.value)}
                  placeholder="Availability, seasonal capacity, average ticket size, what makes your fleet premium…"
                  rows={4}
                  className="bg-zinc-900 border-white/10 text-sm"
                />
              </Field>

              <button
                type="submit"
                data-testid="partner-submit-btn"
                disabled={saving}
                className="w-full h-12 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] disabled:opacity-60 text-black text-[11px] uppercase tracking-[0.25em] font-semibold flex items-center justify-center gap-2 transition-all"
              >
                <Send className="w-4 h-4" />
                {saving ? "Sending…" : "Send Application"}
              </button>

              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-600 text-center">
                Reviewed by our partnerships team · Reply within 2 business days
              </p>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
        {label}{required && <span className="text-[#C5A059] ml-1">*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
